import { config, logger } from '~/app';
import { createBotAvatarSvg, isBotAvatarFilename } from '~/server/src/utils/botAvatarConfig';

export const getProfilePictureUrl = (filename?: string): string | undefined => {
  if (!filename) {
    return undefined;
  }

  // Check if this is a bot avatar filename (bot-*.png)
  const isBotAvatar = isBotAvatarFilename(filename);

  // If it's a bot avatar, serve from bot-avatars directory
  if (isBotAvatar) {
    return `${config.baseUrl}/uploads/bot-avatars/${filename}`;
  }

  // Otherwise, it's a regular profile picture file
  return `${config.baseUrl}/uploads/profile-pictures/${filename}`;
};

export const getInitials = (username: string): string => {
  if (!username) return '';

  const words = username.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a bot avatar data URL on the client side using DiceBear
 * This ensures the same bot always gets the same avatar
 * @param userId - The bot's user ID
 * @returns Promise<string> - Data URL of the generated avatar
 */
export const generateBotAvatarDataUrl = async (userId: string): Promise<string> => {
  try {
    // Create avatar SVG using shared configuration
    const svgString = createBotAvatarSvg(userId);

    // Convert SVG to data URL directly
    const encodedSvg = encodeURIComponent(svgString);
    return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
  } catch (error) {
    logger.warn({ err: error }, 'Error generating bot avatar');
    throw new Error('Failed to generate bot avatar', { cause: error });
  }
};

/**
 * Check if a user ID represents a local bot
 * @param userId - The user ID to check
 * @param isLocalMode - Whether we're in local training mode
 * @returns True if this is a local bot
 */
export const isLocalBot = (userId: string | undefined, isLocalMode: boolean): boolean => {
  return isLocalMode && !!userId && userId.startsWith('bot_');
};
